import { createSlice, current, type PayloadAction } from '@reduxjs/toolkit';
import { isEqual } from 'lodash-es';

import type {
  SearchResponseFilterOutput,
  SearchResultOrder,
} from '@iconik-io/iconik-api/src/services/search';

import type {
  NaturalSearchResultPayload,
  NaturalSearchSession,
  NaturalSearchState,
} from './types';

const initialState: NaturalSearchState = {
  isLoading: false,
  session: null,
};

export const naturalSearchSlice = createSlice({
  name: 'natural_search',
  initialState,
  reducers: {
    setNaturalSearchLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setNaturalSearchResult: (
      state,
      action: PayloadAction<NaturalSearchResultPayload>,
    ) => {
      state.isLoading = false;
      state.session = {
        ...action.payload,
        // Another page of the same answer keeps the frames already resolved.
        proxyUrlByAssetId:
          state.session?.query === action.payload.query
            ? state.session.proxyUrlByAssetId
            : {},
      };
    },

    /** The order the next replay of the answer asks for. */
    setNaturalSearchOrder: (
      state,
      action: PayloadAction<SearchResultOrder>,
    ) => {
      if (state.session?.replay) {
        state.session.replay.filters.order = action.payload;
      }
    },

    /**
     * Makes another reading of the query the one the next replay runs, from its
     * first page: whether it can page is only known once it has answered. The
     * panel is filled from it then, since it is not what the panel holds.
     */
    setNaturalSearchInterpretation: (state, action: PayloadAction<number>) => {
      const { session } = state;
      const filters = session?.interpretations.list[action.payload]?.filters;
      if (!session?.replay || !filters) {
        return;
      }
      session.replay = {
        filters,
        pagination: { supported: false, reason: null },
      };
      session.projection = null;
      session.interpretations.active = action.payload;
      // They were changes to the reading it replaces.
      session.adjustments = { added: [], paused: [] };
    },

    /** Puts back the reading a switch replaced, when its answer never came. */
    restoreNaturalSearchReading: (
      state,
      action: PayloadAction<
        Pick<
          NaturalSearchSession,
          'replay' | 'projection' | 'interpretations' | 'adjustments'
        >
      >,
    ) => {
      if (state.session) {
        Object.assign(state.session, action.payload);
      }
    },

    /** Leaves every reading for the query as typed, so none are offered again. */
    leaveNaturalSearchReadings: (state) => {
      if (state.session) {
        state.session.interpretations.active = null;
        state.session.interpretations.chosen = true;
      }
    },

    /** Keeps the reading on screen, so the others are no longer offered. */
    keepNaturalSearchInterpretation: (state) => {
      if (state.session && state.session.interpretations.active !== null) {
        state.session.interpretations.chosen = true;
      }
    },

    /** Applies filters the answer gave up on, as the next replay asks. */
    reapplyNaturalSearchFilters: (
      state,
      action: PayloadAction<SearchResponseFilterOutput[]>,
    ) => {
      state.session?.adjustments.added.push(
        ...action.payload.map((entry) => ({
          ...entry,
          // Now the user's, which scopes the results.
          role: 'filter' as const,
          reason: null,
        })),
      );
    },

    /**
     * Pauses, or resumes, a filter of the answer the panel does not show, as
     * the next replay asks. Like the eye toggle of a chip, it stays listed.
     */
    setNaturalSearchFilterPaused: (
      state,
      action: PayloadAction<{
        entry: SearchResponseFilterOutput;
        paused: boolean;
      }>,
    ) => {
      const { session } = state;
      if (!session) {
        return;
      }
      const { entry, paused } = action.payload;
      // Compared on a snapshot: the draft's proxies are not plain objects.
      const others = current(session.adjustments.paused).filter(
        (pausedEntry) => !isEqual(pausedEntry, entry),
      );
      session.adjustments.paused = paused ? [...others, entry] : others;
    },

    setNaturalSearchProxyUrl: (
      state,
      action: PayloadAction<{ assetId: string; url: string | null }>,
    ) => {
      if (!state.session) {
        return;
      }
      state.session.proxyUrlByAssetId[action.payload.assetId] =
        action.payload.url;
    },

    /**
     * Resets the state only. Dispatch `endNaturalSearchSession` or
     * `clearNaturalSearchAndCancel` instead unless you have already cancelled:
     * `runNaturalSearch` re-checks `isLoading` before publishing, so a bare clear
     * is still correct, but it leaves an expensive AI call running for a result
     * that will be thrown away.
     */
    clearNaturalSearch: () => initialState,
  },
});

export const {
  clearNaturalSearch,
  keepNaturalSearchInterpretation,
  leaveNaturalSearchReadings,
  reapplyNaturalSearchFilters,
  restoreNaturalSearchReading,
  setNaturalSearchFilterPaused,
  setNaturalSearchInterpretation,
  setNaturalSearchLoading,
  setNaturalSearchOrder,
  setNaturalSearchProxyUrl,
  setNaturalSearchResult,
} = naturalSearchSlice.actions;

export const naturalSearchReducer = naturalSearchSlice.reducer;
